import React, { useContext, useMemo } from 'react';

import { makeStyles } from '@mui/styles';
import {
  Avatar,
  Box,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { PatientTabsContext } from '../../../lib/contexts';
import useMetric from '../../../lib/hooks/useMetric';
import { AdminProgressBar } from '../../../components/AdminProgressBar';
import { ArrowDropDown, ArrowDropUp, EmojiObjects, FlashOn, InfoRounded, Print } from '@mui/icons-material';
import { AdminButton } from '../../../components/AdminButton';
import { roundHalf } from '../../../lib/helpers/helpers';

const useStyles = makeStyles((theme) => ({
  container: {
    // margin:10
  },
  patientChartCard: {
    ...theme.globalClasses.patientChartCard,
    '& p': {
      marginTop: 14,
    },
  },
  title: {
    fontSize: 10,
    '& svg': {
      fill: theme.palette.admin.main,
    },
  },
  list: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: theme.palette.primary.main,
    height: '100%',
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    // '&::after': {
    //     content: '""',
    //     position: 'absolute',
    //     top: 0,
    //     left: 0,
    //     right: 0,
    //     bottom: 0,
    //     border: 'inherit',
    //     borderColor: theme.palette.secondary.main,
    //     opacity: 0.5,
    //     pointerEvents: 'none',
    // },
  },
  listItemAvatar: {
    display: 'block',
    borderRadius: 6,
    width: '25px',
    height: 25,
    backgroundColor: theme.palette.common.increase,
    marginRight: 15,
    '& svg': {
      fill: theme.palette.admin.main,
    },
    '@media print': {
      display: 'none !important',
    },
  },
  listItemAvatarDecrease: {
    display: 'block',
    borderRadius: 6,
    width: '25px',
    height: 25,
    backgroundColor: theme.palette.common.decrease,
    marginRight: 15,
    '& svg': {
      fill: theme.palette.error.dark,
    },
    '@media print': {
      display: 'none !important',
    },
  },
  listItem: {
    margin: 0,
    '& span': {
      margin: '0px !important',
      color: theme.palette.admin.main,
      fontSize: 14,
    },
    '& p': {
      margin: '0px !important',
      color: theme.typography.helper.main,
      fontSize: '12px !important',
    },
  },
  listItemSecondary: {
    '& svg': {
      fill: theme.typography.helper.light,
    },
  },
  itemBorder: {
    paddingTop: 10,
    paddingBottom: 10,
    borderWidth: '0px 1px 0px 0px',
    borderStyle: 'solid',
    borderColor: theme.palette.admin.background,
  },
  item: {
    paddingLeft: 15,
    paddingRight: 15,
    textAlign: 'center',
    '& h6': {
      textTransform: 'uppercase',
    },
    '& p': {
      fontSize: 14,
      marginBottom: 50,
    },
  },
  patientGoalsContainer: {
    height: '100%',
  },
  goalGridItems: {
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2), // Add spacing between stacked elements
    },
  },
  frameCorrection: {
    paddingBottom: (props) => (props.isPhoneView ? 0 : 25),
    marginBottom: (props) => (props.isPhoneView ? 0 : -15),
  },
  topFoodChanges: {
    marginTop: 10,
    marginBottom: 10,
  },
  prioritiesTitle: {
    fontSize: '1.5rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.2rem !important',
    },
  },
}));

const PatientPrioritiesBox = ({ onPrint }) => {
  const theme = useTheme();
  const matchSM = useMediaQuery(theme.breakpoints.up('sm'));
  const matchMD = useMediaQuery(theme.breakpoints.up('md'));
  const matchLG = useMediaQuery(theme.breakpoints.up('lg'));
  const matchXL = useMediaQuery(theme.breakpoints.up('xl'));
  const {
    state: { currentTab },
  } = useContext(PatientTabsContext);
  const { isLoading, data } = useMetric('priorities', currentTab.id);
  const classes = useStyles({ isPhoneView: !matchSM });

  const defaultPrint = () => {
    //console.log('using default print function in patientprioritiesbox')
    window.print();
  };

  return (
    <Grid
      item
      xs={12}
      className={classes.frameCorrection}
    >
      <Paper className={classes.patientChartCard}>
        <Grid
          item
          container
          xs={12}
          spacing={1}
        >
          <Grid
            item
            xs={10}
          >
            <Box mb={2}>
              <Typography
                variant={'h1'}
                className={classes.prioritiesTitle}
              >
                Today&apos;s Priorities for {currentTab.firstName}
              </Typography>
              <Typography variant={'body1'}>
                Top food changes to reach Patient&apos;s goal
                {data && data.metric.value.ideal_diet ? (
                  <>
                    {' '}
                    diet of <b>&quot;{data.metric.value.ideal_diet}&quot;</b>
                  </>
                ) : (
                  ''
                )}
              </Typography>
            </Box>
          </Grid>
          <Grid
            item
            xs={2}
            container
            justifyContent={'flex-end'}
            style={{ textAlign: 'right', height: 44 }}
            className="print-hide"
          >
            <AdminButton
              variant={'outlined'}
              onClick={onPrint || defaultPrint}
            >
              <Print />
              &nbsp;Print
            </AdminButton>
          </Grid>
        </Grid>
        {isLoading ? (
          <AdminProgressBar />
        ) : (
          <>
            {data && data.metric && (
              <Grid
                xs={12}
                spacing={1}
                container
                item
                className={classes.container}
              >
                <Grid
                  item
                  xs={matchXL || matchLG ? 8 : 12}
                  className={`${classes.item} ${classes.itemBorder}`}
                >
                  {data &&
                    data.metric &&
                    data.metric.value &&
                    data.metric.value.food_changes &&
                    data.metric.value.food_changes.increases &&
                    data.metric.value.food_changes.decreases && (
                      <Grid
                        container
                        item
                        xs={12}
                        spacing={matchXL ? 2 : 1}
                        justifyContent={'space-between'}
                        className={classes.patientGoalsContainer}
                      >
                        <Grid
                          item
                          xs={matchXL || matchLG ? 6 : 12}
                          className={classes.goalGridItems}
                        >
                          <Typography
                            variant={'subtitle2'}
                            className={classes.title}
                          >
                            Increase
                          </Typography>
                          <List
                            dense={true}
                            className={classes.list}
                          >
                            {data.metric.value.food_changes.increases.length !== 0 ? (
                              data.metric.value.food_changes.increases.map((item, index) => {
                                return (
                                  <ListItem
                                    key={index}
                                    className={classes.listItem}
                                  >
                                    <Avatar className={classes.listItemAvatar}>
                                      <ArrowDropUp />
                                    </Avatar>
                                    <ListItemText
                                      primary={item.label}
                                      secondary={'by ' + roundHalf(item.change.difference) + ' servings'}
                                    />
                                    <ListItemSecondaryAction className={classes.listItemSecondary}>
                                      {item.change.children && (
                                        <Tooltip title={item.change.children.map((item) => item.label + ' ')}>
                                          <InfoRounded />
                                        </Tooltip>
                                      )}
                                    </ListItemSecondaryAction>
                                  </ListItem>
                                );
                              })
                            ) : (
                              <div>Nothing to Increase</div>
                            )}
                          </List>
                        </Grid>
                        <Grid
                          item
                          xs={matchXL || matchLG ? 6 : 12}
                          className={classes.goalGridItems}
                        >
                          <Typography
                            variant={'subtitle2'}
                            className={classes.title}
                          >
                            Decrease
                          </Typography>
                          <List
                            dense={true}
                            className={classes.list}
                          >
                            {data.metric.value.food_changes.decreases.length !== 0 ? (
                              data.metric.value.food_changes.decreases.map((item, index) => {
                                return (
                                  <ListItem
                                    key={index}
                                    className={classes.listItem}
                                  >
                                    <Avatar className={classes.listItemAvatarDecrease}>
                                      <ArrowDropDown />
                                    </Avatar>
                                    <ListItemText
                                      primary={item.label}
                                      secondary={'by ' + roundHalf(Math.abs(item.change.difference)) + ' servings'}
                                    />
                                  </ListItem>
                                );
                              })
                            ) : (
                              <div>Nothing to Decrease</div>
                            )}
                          </List>
                        </Grid>
                      </Grid>
                    )}
                </Grid>
                <Grid
                  item
                  xs={matchXL || matchLG ? 4 : 12}
                  className={
                    classes.item
                  } /*style={{borderLeft: '1px solid rgba(0,0,0,0.1)', borderTop: '1px solid rgba(0,0,0,0.1)'}}*/
                >
                  <Typography
                    variant={'subtitle2'}
                    className={classes.title}
                  >
                    <FlashOn />
                    <br />
                    This Week`&apos;s Actions
                  </Typography>
                  <Tooltip title={data.metric.value.this_week_action_description}>
                    <Typography>{data.metric.value.this_week_action}</Typography>
                  </Tooltip>
                  <Typography
                    variant={'subtitle2'}
                    className={classes.title}
                  >
                    <EmojiObjects />
                    <br />
                    Today`&apos;s Tip
                  </Typography>
                  <Tooltip title={data.metric.value.todays_tip_description}>
                    <Typography>{data.metric.value.todays_tip}</Typography>
                  </Tooltip>
                </Grid>
              </Grid>
            )}
          </>
        )}
      </Paper>
    </Grid>
  );
};

export default PatientPrioritiesBox;
